import { useState, useEffect, useCallback } from 'react';
import { fetchLaunchPhaseConfig, invalidateLaunchPhaseCache } from '../../lib/launchPhaseClient.js';
import {
  getLaunchBlockForPage,
  isInvitationGateEnabled,
  isLaunchFlagEnabled,
  parseLaunchPhaseConfig,
} from '../utils/launchPhaseConfig.js';

/**
 * Lee `app_config.launch_phase` y expone helpers para bloquear módulos por fase L1–L4.
 * Los administradores no omiten el bloqueo (deben ver lo mismo que testers salvo panel admin).
 */
export function useLaunchPhase() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    invalidateLaunchPhaseCache();
    try {
      const next = await fetchLaunchPhaseConfig();
      setCfg(next);
    } catch (_) {
      setCfg(parseLaunchPhaseConfig(null));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const isFlagEnabled = useCallback(
    flagKey => {
      if (!cfg) return true;
      return isLaunchFlagEnabled(cfg, flagKey);
    },
    [cfg]
  );

  const isPageBlocked = useCallback(
    page => {
      if (!cfg) return false;
      return !!getLaunchBlockForPage(cfg, page);
    },
    [cfg]
  );

  const getBlockedPageMeta = useCallback(
    page => {
      if (!cfg) return null;
      return getLaunchBlockForPage(cfg, page);
    },
    [cfg]
  );

  return {
    cfg,
    loading,
    refresh,
    phase: cfg?.phase ?? 1,
    label: cfg?.label ?? '',
    message: cfg?.message ?? '',
    flags: cfg?.flags ?? {},
    isFlagEnabled,
    isPageBlocked,
    getBlockedPageMeta,
    paymentsEnabled: !!cfg?.flags?.payments_enabled,
    auctionsEnabled: !!cfg?.flags?.auctions_enabled,
    partnerOpsEnabled: !!cfg?.flags?.partner_ops_enabled,
    guestMarketplaceEnabled: !!cfg?.flags?.guest_marketplace_enabled,
    marketplacePartnerLogisticsEnabled: !!cfg?.flags?.marketplace_partner_logistics_enabled,
    invitationGateEnabled: isInvitationGateEnabled(cfg),
  };
}
